import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride';
import { TutorialTooltip } from './TutorialTooltip';
import { TutorialWelcome } from './TutorialWelcome';
import { TutorialComplete } from './TutorialComplete';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';

export type TutorialType = 
  | 'home' 
  | 'marketplace' 
  | 'helicopterWizard' 
  | 'engineWizard' 
  | 'partsWizard' 
  | 'jobWizard'
  | 'serviceWizard'
  | 'profile' 
  | 'messaging'
  | 'verification'
  | 'savedCompare'
  | 'privacy'
  | 'alerts'
  | 'listingManagement';

// Extended Step type with action data for orchestration
export interface TutorialStep extends Step {
  data?: {
    route?: string;
    profileSection?: string;
    dashboardTab?: string;
    wizardStep?: number;
    click?: string;
  };
}

interface TutorialContextType {
  startTutorial: (type: TutorialType) => void;
  stopTutorial: () => void;
  isRunning: boolean;
  currentTutorial: TutorialType | null;
  completedTutorials: TutorialType[];
  markComplete: (type: TutorialType) => void;
  resetTutorial: (type: TutorialType) => void;
  resetAllTutorials: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorialContext = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorialContext must be used within TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY = 'chopperswap_completed_tutorials';

// Tutorials that require authentication
const AUTH_REQUIRED_TUTORIALS: TutorialType[] = ['profile', 'messaging', 'verification', 'savedCompare', 'privacy', 'alerts', 'listingManagement'];

// Map tutorial types to their routes
const TUTORIAL_ROUTES: Partial<Record<TutorialType, string>> = {
  home: '/?asset=helicopter',
  marketplace: '/marketplace',
  profile: '/profile',
  messaging: '/messages',
  verification: '/profile?section=verification',
  savedCompare: '/profile?section=favorites',
  privacy: '/profile?section=settings',
  alerts: '/profile?section=alerts',
  listingManagement: '/profile?section=dashboard',
};

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Core state
  const [isRunning, setIsRunning] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState<TutorialType | null>(null);
  const [completionTutorial, setCompletionTutorial] = useState<TutorialType | null>(null); // Separate for completion modal
  const [wasSkipped, setWasSkipped] = useState(false);
  const [steps, setSteps] = useState<TutorialStep[]>([]);
  const [completedTutorials, setCompletedTutorials] = useState<TutorialType[]>([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isPreparing, setIsPreparing] = useState(false);
  
  // Run ID for cancellation - incremented on each new tutorial start
  const runIdRef = useRef(0);
  // Track current preparation to prevent race conditions
  const activePreparationRef = useRef<number | null>(null);
  // Track if finalization has happened for this run
  const finalizedRunIdRef = useRef<number | null>(null);
  // Observer cleanup
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const watchdogRef = useRef<number | null>(null);
  const hasSeenTooltipRef = useRef(false);

  // Load completed tutorials from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setCompletedTutorials(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse completed tutorials:', e);
      }
    }
  }, []);

  // Sync with localStorage when completedTutorials changes
  useEffect(() => {
    if (completedTutorials.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completedTutorials));
    }
  }, [completedTutorials]);

  // Cleanup function for observers and timeouts
  const cleanupAsyncOperations = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
    activePreparationRef.current = null;
  }, []);

  

  // Hide cookie banner during active tutorials
  useEffect(() => {
    if (isRunning) {
      document.body.classList.add('tutorial-active');
    } else {
      document.body.classList.remove('tutorial-active');
    }
    return () => document.body.classList.remove('tutorial-active');
  }, [isRunning]);

  // Wait for an element to appear in the DOM with MutationObserver
  const waitForElement = useCallback((selector: string, runId: number, maxWait = 3000): Promise<Element | null> => {
    return new Promise((resolve) => {
      // Check if this run is still active
      if (runIdRef.current !== runId) {
        return resolve(null);
      }

      // Check if already exists
      const element = document.querySelector(selector);
      if (element) {
        return resolve(element);
      }
      
      // Cleanup any previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      // Use MutationObserver for efficient DOM watching
      const observer = new MutationObserver(() => {
        if (runIdRef.current !== runId) {
          observer.disconnect();
          return resolve(null);
        }
        
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          observerRef.current = null;
          resolve(el);
        }
      });
      
      observerRef.current = observer;
      observer.observe(document.body, { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['data-tutorial-id', 'data-tutorial-step']
      });
      
      // Timeout fallback
      timeoutRef.current = window.setTimeout(() => {
        observer.disconnect();
        observerRef.current = null;
        
        if (runIdRef.current !== runId) {
          resolve(null);
        } else {
          resolve(document.querySelector(selector));
        }
      }, maxWait);
    });
  }, []);

  // Navigate wizard to specific step by clicking stepper button
  const navigateWizardToStep = useCallback(async (stepNumber: number, runId: number): Promise<boolean> => {
    if (runIdRef.current !== runId) return false;
    
    // Scope to the wizard stepper container to avoid clicking hidden mobile elements
    const stepButton = document.querySelector(
      `[data-tutorial-id="wizard-stepper"] [data-tutorial-step="${stepNumber}"]`
    ) as HTMLElement;
    
    if (stepButton) {
      stepButton.click();
      await new Promise(r => setTimeout(r, 500));
      
      // Wait for the step content to update
      const contentElement = document.querySelector('[data-tutorial-id="wizard-step-content"]');
      if (!contentElement) {
        // Wait a bit more for content to render
        await new Promise(r => setTimeout(r, 300));
      }
      
      return runIdRef.current === runId;
    }
    
    return false;
  }, []);

  // Click a dashboard tab - use both URL navigation and direct click for reliability
  const clickDashboardTab = useCallback(async (tabName: string, runId: number): Promise<boolean> => {
    if (runIdRef.current !== runId) return false;
    
    // First ensure we're on the dashboard section
    const currentUrl = location.pathname + location.search;
    if (!currentUrl.includes('section=dashboard')) {
      navigate(`/profile?section=dashboard&tab=${tabName}`);
      await new Promise(r => setTimeout(r, 500));
      if (runIdRef.current !== runId) return false;
    }
    
    // Then click the tab directly for immediate feedback
    const tabTrigger = document.querySelector(`[data-tutorial-id="dashboard-tab-${tabName}"]`) as HTMLElement;
    if (tabTrigger) {
      tabTrigger.click();
      await new Promise(r => setTimeout(r, 300));
    }
    
    // Wait for tab content to appear
    const tabContent = await waitForElement(`[data-tutorial-id="dashboard-content-${tabName}"]`, runId, 2500);
    
    return tabContent !== null && runIdRef.current === runId;
  }, [navigate, location.pathname, location.search, waitForElement]);

  // Click a profile menu item - always use URL navigation for reliability
  const clickProfileSection = useCallback(async (sectionName: string, runId: number): Promise<boolean> => {
    if (runIdRef.current !== runId) return false;
    
    // Navigate via URL for 100% reliability
    navigate(`/profile?section=${sectionName}`);
    await new Promise(r => setTimeout(r, 400));
    
    return runIdRef.current === runId;
  }, [navigate]);

  // Prepare for a step by performing necessary actions BEFORE Joyride renders
  const prepareForStep = useCallback(async (step: TutorialStep, index: number, runId: number): Promise<boolean> => {
    // Check if this is the active run
    if (runIdRef.current !== runId) {
      return false;
    }
    
    // Prevent concurrent preparations
    if (activePreparationRef.current === index) {
      return true;
    }
    
    activePreparationRef.current = index;
    setIsPreparing(true);

    try {
      const data = step.data;
      
      // 1. Handle route navigation
      if (data?.route) {
        const targetPath = data.route.split('?')[0];
        if (!location.pathname.includes(targetPath)) {
          navigate(data.route);
          await new Promise(r => setTimeout(r, 500));
          if (runIdRef.current !== runId) return false;
        }
      }
      
      // 2. Handle profile section navigation
      if (data?.profileSection) {
        const success = await clickProfileSection(data.profileSection, runId);
        if (!success && runIdRef.current !== runId) return false;
      }
      
      // 3. Handle dashboard tab clicking
      if (data?.dashboardTab) {
        await clickDashboardTab(data.dashboardTab, runId);
        if (runIdRef.current !== runId) return false;
      }
      
      // 4. Handle wizard step navigation
      if (data?.wizardStep !== undefined) {
        await navigateWizardToStep(data.wizardStep, runId);
        if (runIdRef.current !== runId) return false;
      }
      
      // 5. Handle generic click action
      if (data?.click) {
        const clickTarget = document.querySelector(data.click) as HTMLElement;
        if (clickTarget) {
          clickTarget.click();
          await new Promise(r => setTimeout(r, 300));
          if (runIdRef.current !== runId) return false;
        }
      }

      // Wait for target element to appear
      const target = step.target as string;
      const element = await waitForElement(target, runId, 2500);
      
      if (runIdRef.current !== runId) return false;
      
      return element !== null;
    } catch (error) {
      console.error('Error preparing step:', error);
      return runIdRef.current === runId;
    } finally {
      if (runIdRef.current === runId) {
        setIsPreparing(false);
        activePreparationRef.current = null;
      }
    }
  }, [navigate, location.pathname, waitForElement, clickProfileSection, clickDashboardTab, navigateWizardToStep]);

  const loadTutorialSteps = async (type: TutorialType): Promise<TutorialStep[]> => {
    switch (type) {
      case 'home':
        const { getHomeTutorialSteps } = await import('@/data/tutorials/homeTutorial');
        return getHomeTutorialSteps();
      case 'marketplace':
        const { getMarketplaceTutorialSteps } = await import('@/data/tutorials/marketplaceTutorial');
        return getMarketplaceTutorialSteps();
      case 'helicopterWizard':
      case 'engineWizard':
      case 'partsWizard':
      case 'jobWizard':
      case 'serviceWizard':
        const { getWizardTutorialSteps } = await import('@/data/tutorials/wizardTutorials');
        return getWizardTutorialSteps(type);
      case 'profile':
        const { getProfileTutorialSteps } = await import('@/data/tutorials/profileTutorial');
        return getProfileTutorialSteps();
      case 'messaging':
        const { getMessagingTutorialSteps } = await import('@/data/tutorials/messagingTutorial');
        return getMessagingTutorialSteps();
      case 'verification':
        const { verificationTutorialSteps } = await import('@/data/tutorials/verificationTutorial');
        return verificationTutorialSteps;
      case 'savedCompare':
        const { savedCompareTutorialSteps } = await import('@/data/tutorials/savedCompareTutorial');
        return savedCompareTutorialSteps;
      case 'privacy':
        const { privacyTutorialSteps } = await import('@/data/tutorials/privacyTutorial');
        return privacyTutorialSteps;
      case 'alerts':
        const { getAlertsTutorialSteps } = await import('@/data/tutorials/alertsTutorial');
        return getAlertsTutorialSteps();
      case 'listingManagement':
        const { dashboardTutorialSteps } = await import('@/data/tutorials/dashboardTutorial');
        return dashboardTutorialSteps;
      default:
        return [];
    }
  };

  // Complete stop and cleanup
  const stopTutorial = useCallback(() => {
    // Increment runId to invalidate any pending async operations
    runIdRef.current += 1;
    
    // Cleanup observers and timeouts
    cleanupAsyncOperations();
    
    // CRITICAL: Reset finalization guard to allow new tutorials to start
    finalizedRunIdRef.current = null;
    
    // Reset tooltip tracking for next tutorial
    hasSeenTooltipRef.current = false;
    
    // Reset all state including completion modal
    setIsRunning(false);
    setCurrentTutorial(null);
    setCompletionTutorial(null);
    setSteps([]);
    setStepIndex(0);
    setIsPreparing(false);
    setShowWelcome(false);
    setShowComplete(false);
  }, [cleanupAsyncOperations]);

  // Finalize tutorial with completion modal
  const finalizeTutorial = useCallback((type: TutorialType, shouldMarkComplete: boolean = true) => {
    const thisRunId = runIdRef.current;
    
    // Prevent double-finalization for the same run
    if (finalizedRunIdRef.current === thisRunId) {
      return;
    }
    finalizedRunIdRef.current = thisRunId;
    
    // Increment runId to stop any pending operations
    runIdRef.current += 1;
    cleanupAsyncOperations();
    
    // Stop running
    setIsRunning(false);
    setIsPreparing(false);
    
    // Store the type and skip state for the completion modal
    setCompletionTutorial(type);
    setWasSkipped(!shouldMarkComplete);
    
    // Mark complete if applicable
    if (shouldMarkComplete) {
      setCompletedTutorials(prev => {
        if (prev.includes(type)) return prev;
        const updated = [...prev, type];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    }
    
    // Show completion modal
    setShowComplete(true);
  }, [cleanupAsyncOperations]);

  // Watchdog: auto-finalize if Joyride hangs (no tooltip visible for 6s while running)
  useEffect(() => {
    if (!isRunning || isPreparing) {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
      return;
    }

    // Check if tooltip is currently visible — if so, mark as seen
    const tooltipNow = document.querySelector('.__floater');
    if (tooltipNow) {
      hasSeenTooltipRef.current = true;
    }

    // Only arm watchdog after at least one tooltip has been rendered
    if (!hasSeenTooltipRef.current) {
      // Re-check after a short delay (tooltip may still be mounting)
      watchdogRef.current = window.setTimeout(() => {
        const tooltip = document.querySelector('.__floater');
        if (tooltip) {
          hasSeenTooltipRef.current = true;
        }
      }, 2000);
      return () => {
        if (watchdogRef.current) {
          clearTimeout(watchdogRef.current);
          watchdogRef.current = null;
        }
      };
    }

    watchdogRef.current = window.setTimeout(() => {
      const tooltip = document.querySelector('.__floater');
      if (!tooltip && isRunning && currentTutorial) {
        console.warn('[Tutorial] Watchdog: no tooltip visible for 6s, auto-finalizing');
        toast({
          title: "Tutorial interrupted",
          description: "A step couldn't be displayed. You can restart from the help menu.",
        });
        finalizeTutorial(currentTutorial, false);
      }
    }, 6000);

    return () => {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, [isRunning, isPreparing, stepIndex, currentTutorial, toast, finalizeTutorial]);

  const startTutorial = useCallback(async (type: TutorialType) => {
    try {
      console.log('[Tutorial] Starting tutorial:', type);
      
      // Stop any currently running tutorial first
      stopTutorial();
      
      // Give React time to flush stop state updates before starting new tutorial
      await new Promise(r => setTimeout(r, 50));
      
      // Increment run ID for this new tutorial
      runIdRef.current += 1;
      const thisRunId = runIdRef.current;
      
      console.log('[Tutorial] Run ID:', thisRunId);
      
      // Check authentication for protected tutorials
      if (AUTH_REQUIRED_TUTORIALS.includes(type) && !user) {
        toast({
          title: "Sign in required",
          description: "Please sign in to access this tutorial.",
        });
        navigate('/auth');
        return;
      }
      
      // Navigate to correct route if needed - compare full URL including query params
      const targetRoute = TUTORIAL_ROUTES[type];
      if (targetRoute) {
        const currentUrl = location.pathname + location.search;
        const targetPath = targetRoute.split('?')[0];
        
        // Navigate if on wrong pathname OR if query params don't match for profile-based tutorials
        // Use exact match for '/' to avoid false positives (every path starts with '/')
        const needsNavigation = targetPath === '/'
          ? (location.pathname !== '/' || (targetRoute.includes('?') && currentUrl !== targetRoute))
          : !location.pathname.startsWith(targetPath) || 
            (targetRoute.includes('?') && !currentUrl.includes(targetRoute.split('?')[1]?.split('=')[1] || ''));
        
        if (needsNavigation) {
          navigate(targetRoute);
          await new Promise(r => setTimeout(r, 600));
          
          // Check if still valid after navigation
          if (runIdRef.current !== thisRunId) return;
        }
      }
      
      // Load tutorial steps
      const tutorialSteps = await loadTutorialSteps(type);
      
      console.log('[Tutorial] Steps loaded:', tutorialSteps.length);
      
      if (runIdRef.current !== thisRunId) return;
      
      if (tutorialSteps.length === 0) {
        toast({
          title: "Tutorial unavailable", 
          description: "No tutorial steps available for this section.",
          variant: "destructive",
        });
        return;
      }

      // Set up tutorial state synchronously
      setSteps(tutorialSteps);
      setCurrentTutorial(type);
      setStepIndex(0);
      
      const isFirstTime = !completedTutorials.includes(type);
      console.log('[Tutorial] Is first time:', isFirstTime);
      
      // Show welcome modal for first-time tutorials
      if (isFirstTime) {
        // Use microtask to ensure state is set before showing welcome
        queueMicrotask(() => {
          if (runIdRef.current === thisRunId) {
            setShowWelcome(true);
          }
        });
      } else {
        // Prepare first step then start
        const prepared = await prepareForStep(tutorialSteps[0], 0, thisRunId);
        if (runIdRef.current !== thisRunId) return;
        
        if (prepared) {
          setIsRunning(true);
        } else {
          toast({
            title: "Tutorial unavailable",
            description: "Could not find the tutorial elements. Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Failed to start tutorial:', error);
      toast({
        title: "Error",
        description: "Failed to start tutorial. Please try again.",
        variant: "destructive",
      });
    }
  }, [completedTutorials, toast, prepareForStep, navigate, location.pathname, location.search, user, stopTutorial]);

  const markComplete = useCallback((type: TutorialType) => {
    setCompletedTutorials(prev => {
      if (prev.includes(type)) return prev;
      const updated = [...prev, type];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const resetTutorial = useCallback((type: TutorialType) => {
    setCompletedTutorials(prev => {
      const updated = prev.filter(t => t !== type);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const resetAllTutorials = useCallback(() => {
    setCompletedTutorials([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const handleWelcomeStart = async () => {
    setShowWelcome(false);
    
    const thisRunId = runIdRef.current;
    
    if (steps.length > 0) {
      const prepared = await prepareForStep(steps[0], 0, thisRunId);
      if (runIdRef.current !== thisRunId) return;
      
      if (prepared) {
        setIsRunning(true);
      } else {
        toast({
          title: "Tutorial unavailable",
          description: "Could not find the tutorial elements. Please try again.",
          variant: "destructive",
        });
        stopTutorial();
      }
    }
  };

  const handleWelcomeSkip = () => {
    setShowWelcome(false);
    stopTutorial();
  };

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { action, index, status, type: eventType } = data;
    const thisRunId = runIdRef.current;

    // Handle step transitions
    if (eventType === EVENTS.STEP_AFTER || eventType === EVENTS.TARGET_NOT_FOUND) {
      const isForward = action === ACTIONS.NEXT || action === ACTIONS.CLOSE;
      const nextIndex = index + (isForward ? 1 : -1);
      
      // Check if we've reached the end
      if (nextIndex >= steps.length) {
        if (currentTutorial) {
          finalizeTutorial(currentTutorial, true);
        }
        return;
      }
      
      // Prepare next step if it exists
      if (nextIndex >= 0 && nextIndex < steps.length) {
        setIsRunning(false); // Pause while preparing
        
        const prepared = await prepareForStep(steps[nextIndex], nextIndex, thisRunId);
        
        if (runIdRef.current !== thisRunId) return;
        
        if (!prepared && eventType === EVENTS.TARGET_NOT_FOUND) {
          // Skip this step if target not found after preparation
          toast({
            title: "Step skipped",
            description: "Moving to the next available step.",
          });
        }
        
        setStepIndex(nextIndex);
        setIsRunning(true);
      }
    }

    // Tutorial finished successfully
    if (status === STATUS.FINISHED) {
      if (currentTutorial) {
        finalizeTutorial(currentTutorial, true);
      }
    }

    // Tutorial skipped via skip button
    if (status === STATUS.SKIPPED || (eventType === EVENTS.TARGET_NOT_FOUND && action === ACTIONS.CLOSE)) {
      if (currentTutorial) {
        finalizeTutorial(currentTutorial, false);
      }
    }
  };

  const handleCompleteClose = () => {
    setShowComplete(false);
    setCompletionTutorial(null);
    setCurrentTutorial(null);
    setSteps([]);
    setStepIndex(0);
    // Reset finalization guard for next tutorial
    finalizedRunIdRef.current = null;
  };

  return (
    <TutorialContext.Provider
      value={{
        startTutorial,
        stopTutorial,
        isRunning,
        currentTutorial,
        completedTutorials,
        markComplete,
        resetTutorial,
        resetAllTutorials,
      }}
    >
      {children}

      {/* Loading overlay during step preparation */}
      {isPreparing && (isRunning || showWelcome) && (
        <div className="fixed inset-0 bg-black/20 z-[99998] flex items-center justify-center">
          <div className="bg-white dark:bg-slate-900 rounded-xl px-6 py-4 shadow-2xl flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Navigating...</span>
          </div>
        </div>
      )}

      {/* Dark mode arrow color override */}
      <style>{`
        .dark { --joyride-arrow-color: #0f172a; }
        :root { --joyride-arrow-color: #ffffff; }
        .dark .__floater__arrow polygon { fill: #0f172a !important; }
        body.tutorial-active [class*="cookie"],
        body.tutorial-active [id*="cookie"],
        body.tutorial-active [class*="consent"],
        body.tutorial-active [id*="consent"] {
          display: none !important;
        }
      `}</style>

      <Joyride
        steps={steps}
        run={isRunning && !isPreparing}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        scrollOffset={150}
        scrollDuration={400}
        disableOverlayClose
        spotlightClicks
        callback={handleJoyrideCallback}
        tooltipComponent={TutorialTooltip}
        styles={{
          options: {
            arrowColor: 'var(--joyride-arrow-color, #ffffff)',
            backgroundColor: '#ffffff',
            overlayColor: 'rgba(0, 0, 0, 0.75)',
            primaryColor: 'hsl(205, 87%, 21%)',
            textColor: '#1e293b',
            width: 420,
            zIndex: 99999,
          },
          spotlight: {
            borderRadius: 12,
            boxShadow: '0 0 0 4px rgba(30, 64, 175, 0.4), 0 0 20px 8px rgba(30, 64, 175, 0.15)',
          },
        }}
        floaterProps={{
          hideArrow: false,
          disableAnimation: false,
          offset: 16,
        }}
      />

      <TutorialWelcome
        isOpen={showWelcome}
        tutorialType={currentTutorial}
        stepCount={steps.length}
        onStart={handleWelcomeStart}
        onSkip={handleWelcomeSkip}
      />

      <TutorialComplete
        isOpen={showComplete}
        tutorialType={completionTutorial}
        wasSkipped={wasSkipped}
        onClose={handleCompleteClose}
      />
    </TutorialContext.Provider>
  );
};
